import { Input, Popover, Tooltip } from 'antd';
import { useContext, useMemo, useState } from 'react';
import { ProductPPG, Metric } from 'types/tradePromoOptimization';
import { TPODataContext } from 'contexts/TPOContext';
import { InfoCircleOutlined } from '@ant-design/icons';
import { colorConstants } from 'config/colorConstants';
import KPISelector, { KPIItem } from './KPISelector';
import { handleMetricInputChange, handleNumberInputKeyDown, isValidMetricInput } from '../../tpoHelperMethods';
import _ from 'lodash';
import MetricCell from './MetricSection/MetricCell';
import TotalsCell from './MetricSection/TotalsCell';

const PromoMetrics = ({ promoSyncId, showTotals }: { promoSyncId: string; showTotals: boolean }) => {
    const {
        updateSelectedEventProperty,
        selectedEventInfo,
        viewMode,
        isCurrentPromoFormDisabled,
        isTpmTab,
        promoConfigDictionary,
    } = useContext(TPODataContext);

    const [editingCell, setEditingCell] = useState<{ metricId: number; ppgName: string } | null>(null);
    const [editValue, setEditValue] = useState('');
    const combinedViewMode = viewMode || isTpmTab || isCurrentPromoFormDisabled;

    const currentPromo = selectedEventInfo?.currentPromoTactic;

    const isInputDisabled = (metricId: number) =>
        !promoConfigDictionary.promoMetricMapping?.[Number(currentPromo?.promoType)]?.promoMetrics?.[metricId]
            ?.editable;

    const metricKPIs: KPIItem[] = useMemo(() => {
        const kpis =
            currentPromo?.ppgs[0]?.metrics.map((m: Metric) => ({
                value: m.id?.toString(),
                label: m.name,
                isSelected: m.isSelected,
                isHidden: m.isHidden,
            })) || [];

        const orderMap: Record<number, number> = Object.entries(promoConfigDictionary?.promoMetrics || {}).reduce(
            (acc, [key, value]) => {
                acc[value.id] = value.orderId ?? 0;
                return acc;
            },
            {} as Record<number, number>,
        );
        return kpis.sort((a, b) => {
            const orderA = orderMap[Number(a.value)] ?? 0;
            const orderB = orderMap[Number(b.value)] ?? 0;
            return orderA - orderB;
        });
    }, [currentPromo?.ppgs, promoConfigDictionary?.promoMetrics]);

    const metricKPIsForSelector: KPIItem[] = useMemo(() => {
        if (currentPromo?.promoType) {
            const avlMetrics = promoConfigDictionary?.promoMetricMapping[currentPromo.promoType]?.promoMetrics;
            const kpis =
                currentPromo?.ppgs[0]?.metrics.map((m: Metric) => ({
                    value: m.id?.toString(),
                    label: m.name,
                    isSelected: m.isSelected,
                    groupName: avlMetrics[m.id]?.groupName || '',
                    isHidden: m.isHidden,
                })) || [];
            const allGroups = Array.from(new Set(kpis.map((kpi) => kpi.groupName)));

            return allGroups.sort().map((groupName) => ({
                value: groupName,
                label: groupName,
                isSelected: kpis.some((kpi) => kpi.groupName === groupName && kpi.isSelected),
                isHidden: kpis.some((kpi) => kpi.groupName === groupName && kpi.isHidden),
            }));
        }
        return [];
    }, [metricKPIs]);

    const handleInputChange = (metricId: number, productName: string, value: string) => {
        if (!selectedEventInfo?.currentPromoTactic) return;

        const updatedPPGs: any = handleMetricInputChange(
            promoConfigDictionary,
            selectedEventInfo,
            metricId,
            productName,
            value,
            true,
        );

        if (updatedPPGs)
            // Save changes
            updateSelectedEventProperty('currentPromoTactic', {
                ...selectedEventInfo?.currentPromoTactic,
                ppgs: updatedPPGs,
                hasChanges: true,
            });
    };

    const handleTotalInputChange = (metricId: number, productName: string, value: string) => {
        // todo: not required for promo metric, make generic code change
        return;
    };

    const [isPopoverVisible, setIsPopoverVisible] = useState(false);

    const onMetricKPISelectHandler = (updatedGroups: string[]) => {
        // `updatedGroups` contains selected groupNames
        const avlMetrics = promoConfigDictionary?.promoMetricMapping[currentPromo?.promoType || 1]?.promoMetrics;
        selectedEventInfo?.currentPromoTactic.ppgs.forEach((ppg: ProductPPG) => {
            ppg.metrics.forEach((metric: Metric) => {
                metric.isSelected = updatedGroups.includes(avlMetrics[metric.id].groupName);
            });
        });

        // enable save button on change of kpis
        if (!viewMode && selectedEventInfo) {
            selectedEventInfo.currentPromoTactic.hasChanges = true;
        }

        if (selectedEventInfo?.currentPromoTactic) {
            updateSelectedEventProperty('currentPromoTactic', _.cloneDeep(selectedEventInfo.currentPromoTactic));
        }
    };

    return (
        <>
            <div className=" flex">
                <div className="flex flex-col min-w-[240px]  max-w-[240px] ">
                    {metricKPIs
                        ?.filter((m: KPIItem) => m.isSelected && !m.isHidden)
                        ?.map((metric: KPIItem, index: number) => {
                            return (
                                <Tooltip
                                    placement="top"
                                    color="white"
                                    destroyTooltipOnHide
                                    overlayStyle={{ borderRadius: 4 }}
                                    overlayInnerStyle={{
                                        color: colorConstants.darkGrey,
                                    }}
                                    title={metric.label}
                                >
                                    <div
                                        className={`block truncate h-9 border-l border-r border-b p-2 font-xs border-backgroundGreyAlt1 bg-white
                                        ${isInputDisabled(Number(metric.value)) ? 'text-backgroundGreyAlt2' : ''}
                                        `}
                                    >
                                        {metric.label}
                                    </div>
                                </Tooltip>
                            );
                        })}
                </div>

                <div className={`flex flex-grow overflow-x-scroll  hide-scrollable ${promoSyncId} `}>
                    {showTotals && currentPromo?.ppgs && (
                        <TotalsCell
                            key={1}
                            ppgs={currentPromo?.ppgs}
                            headerKPIs={metricKPIs}
                            promoConfigDictionary={promoConfigDictionary}
                            isInputDisabled={isInputDisabled}
                            combinedViewMode={combinedViewMode}
                            editingCell={editingCell}
                            setEditingCell={setEditingCell}
                            editValue={editValue}
                            setEditValue={setEditValue}
                            isValidMetricInput={isValidMetricInput}
                            handleInputChange={handleTotalInputChange}
                            metricType="metrics"
                        />
                    )}
                    {currentPromo?.ppgs
                        ?.filter((pm: ProductPPG) => pm.isSelected)
                        ?.map((pm: ProductPPG) => {
                            return (
                                <MetricCell
                                    key={pm.id}
                                    pm={pm}
                                    headerKPIs={metricKPIs}
                                    promoConfigDictionary={promoConfigDictionary}
                                    isInputDisabled={isInputDisabled}
                                    combinedViewMode={combinedViewMode}
                                    editingCell={editingCell}
                                    setEditingCell={setEditingCell}
                                    editValue={editValue}
                                    setEditValue={setEditValue}
                                    isValidMetricInput={isValidMetricInput}
                                    handleInputChange={handleInputChange}
                                    metricType="metrics"
                                />
                            );
                        })}
                </div>
            </div>
            <div className="bg-backgroundGreyAlt  px-2 py-1 relative w-full">
                <div className="inline-flex justify-start items-center gap-4 cursor-pointer">
                    <KPISelector
                        isVisible={isPopoverVisible}
                        setIsVisible={setIsPopoverVisible}
                        kpiList={metricKPIsForSelector.filter((item) => !item.isHidden) || []}
                        onSubmit={onMetricKPISelectHandler}
                        buttonName={'Add / Remove Metrics'}
                        searchPlaceholder="Search KPI..."
                        variableName="Metric"
                        disableMinimumOneSelectionCondition
                    />
                </div>
            </div>
        </>
    );
};

export default PromoMetrics;
